import { useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Loader2, Sparkles, Copy, Check } from 'lucide-react';
import { reportsApi } from '../services/api';

const TONES = [
  { value: 'professional', label: 'Professional (audit-ready)' },
  { value: 'warm', label: 'Warm & family-facing' },
  { value: 'concise', label: 'Concise & factual' },
  { value: 'clinical', label: 'Clinical (allied-health handover)' },
  { value: 'family_friendly', label: 'Family-friendly (minimal jargon)' },
];

const LENGTHS = [
  { value: 'short', label: 'Short (~120 words)' },
  { value: 'standard', label: 'Standard (~180 words)' },
  { value: 'detailed', label: 'Detailed (~300 words)' },
];

export function RegenerateSummaryDialog({ open, onOpenChange, bundle, onUpdated }) {
  const [tone, setTone] = useState(bundle?.ai_summary_tone || 'professional');
  const [length, setLength] = useState(bundle?.ai_summary_length || 'standard');
  const [customPrompt, setCustomPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    setResult(null);
    try {
      const { data } = await reportsApi.regenerateBundleSummary(bundle.id, {
        tone,
        length,
        custom_prompt: customPrompt.trim() || null,
      });
      setResult(data);
      onUpdated?.(bundle.id, data);
      toast.success('Executive summary regenerated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to regenerate summary');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    const text = result?.summary || bundle?.ai_summary || '';
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Copy failed');
    }
  };

  const currentText = result?.summary || bundle?.ai_summary;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto" data-testid="regenerate-summary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-teal-600" />
            Regenerate Executive Summary
          </DialogTitle>
          <DialogDescription>
            Re-run the AI summary for <strong>{bundle?.client_name}</strong> with a different tone or length.
            The saved PDF stays unchanged — only the summary text is refreshed.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <div>
            <Label className="text-sm font-medium">Tone</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger data-testid="regen-tone-select" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TONES.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium">Length</Label>
            <Select value={length} onValueChange={setLength}>
              <SelectTrigger data-testid="regen-length-select" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LENGTHS.map(l => (<SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-sm font-medium">
            Extra instructions <span className="text-slate-400 font-normal">(optional)</span>
          </Label>
          <Textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="e.g. Highlight cultural safety more prominently; mention the GP follow-up in paragraph 2."
            className="mt-1"
            rows={3}
            maxLength={800}
            data-testid="regen-custom-prompt"
          />
        </div>

        <Button
          onClick={handleGenerate}
          disabled={generating}
          className="mt-4 bg-teal-600 hover:bg-teal-700 w-full"
          data-testid="regen-generate-btn"
        >
          {generating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating with Claude…</>) : (<><Sparkles className="h-4 w-4 mr-2" /> Regenerate summary</>)}
        </Button>

        {currentText && (
          <div className="mt-5 border border-teal-100 bg-teal-50/50 rounded-lg p-4 relative" data-testid="regen-result">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-heading text-sm font-semibold text-slate-900">
                {result ? 'New summary' : 'Current stored summary'}
              </h4>
              <Button size="sm" variant="ghost" onClick={handleCopy} data-testid="regen-copy-btn">
                {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed">
              {currentText}
            </div>
            {result && (
              <p className="text-xs text-slate-500 mt-3">
                Tone: {result.tone} · Length: {result.length} · Regenerated {new Date(result.regenerated_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
